import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Eye,
  FolderOpen,
  Search,
  Filter,
  Edit2,
  Check,
  X
} from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useDocuments } from '@/hooks/useDocuments';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { useAuth } from '@/auth';
import toast from 'react-hot-toast';
import { isFileTypeSupported } from '@/common/components/FilePreview';
import { PreviewDocumentDialog } from './PreviewDocumentDialog';

interface DocumentsTabProps {
  tramite: any
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ tramite }) => {
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const {
    canViewDocuments,
    canUploadDocuments,
    canManageDocuments
  } = useAuthorization();

  const { user } = useAuth();

  const canEditDocument = (document: any) => {
    return canManageDocuments || (canUploadDocuments && document.user.id === user?.id);
  };

  const canDeleteDocument = (document: any) => {
    return canManageDocuments || (canUploadDocuments && document.user.id === user?.id);
  };

  const {
    documents,
    isLoading,
    isUploading,
    isUpdating,
    uploadFiles,
    deleteDocument,
    updateDocumentName,
    downloadDocument,
    formatFileSize,
    getFileIcon,
    getFileTypeLabel,
    validateFile
  } = useDocuments({ tramiteId: tramite.id });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFiles = async (files: FileList) => {
    if (!canUploadDocuments && !canManageDocuments) {
      toast.error('No tienes permisos para subir documentos');
      return;
    }

    // Validar archivos antes de subir
    for (let i = 0; i < files.length; i++) {
      const validation = validateFile(files[i]);
      if (!validation.valid) {
        toast.error(`Error en ${files[i].name}: ${validation.error}`);
        return;
      }
    }

    try {
      await uploadFiles(files);
      toast.success('Archivos subidos correctamente');
    } catch (error) {
      toast.error('Error al subir archivos. Inténtalo de nuevo.');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleDelete = async (documentId: number, document: any) => {
    try {
      await deleteDocument(documentId);
      toast.success('Documento eliminado correctamente');
    } catch (error) {
      toast.error('Error al eliminar el documento. Inténtalo de nuevo.');
    }
  };

  const handleDownload = async (document: any) => {
    try {
      await downloadDocument(document);
      toast.success('Documento descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar el documento. Inténtalo de nuevo.');
    }
  };

  const handlePreview = (document: any) => {
    setPreviewDocument(document);
    setPreviewDialogOpen(true);
  };

  const handleEditStart = (document: any) => {
    setEditingDocumentId(document.id);
    setEditingName(document.name);
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
  };

  const handleEditCancel = () => {
    setEditingDocumentId(null);
    setEditingName('');
  };

  const handleEditSave = async (documentId: number, document: any) => {
    if (!editingName.trim()) {
      toast.error('El nombre del documento no puede estar vacío');
      return;
    }

    try {
      await updateDocumentName(documentId, editingName.trim());
      setEditingDocumentId(null);
      setEditingName('');
      toast.success('Nombre del documento actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el nombre del documento. Inténtalo de nuevo.');
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, documentId: number, document: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave(documentId, document);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || doc.type.includes(filterType);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Documentos del Trámite
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Sube y gestiona los documentos relacionados con este trámite
          </p>
        </div>
        <Badge variant="outline" className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {documents.length} documento{documents.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Search and Filter - Only show if user can view documents */}
      {canViewDocuments && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="word">Word</SelectItem>
              <SelectItem value="excel">Excel</SelectItem>
              <SelectItem value="image">Imágenes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Documents List - Only show if user can view documents */}
      {canViewDocuments && (
        <div className="space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-12 h-12 rounded" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <div className="flex items-center gap-4">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-5 w-12 rounded-full" />
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="w-8 h-8 rounded" />
                        <Skeleton className="w-8 h-8 rounded" />
                        <Skeleton className="w-8 h-8 rounded" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Uploading State */}
          {isUploading && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded flex items-center justify-center bg-blue-100">
                    <Upload className="w-6 h-6 text-blue-600 animate-pulse" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-1/2" />
                      <Badge variant="secondary" className="animate-pulse">Subiendo...</Badge>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full animate-pulse w-1/3"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!isLoading && filteredDocuments.map((document) => (
            <Card key={document.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
                    {React.createElement(getFileIcon(document.type), { className: "w-6 h-6 text-gray-600" })}
                  </div>

                  <div className="flex-1 min-w-0">
                    {editingDocumentId === document.id ? (
                      <div className="flex items-center gap-2 mb-1">
                        <Input
                          ref={editInputRef}
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => handleEditKeyDown(e, document.id, document)}
                          className="text-sm font-medium h-8"
                          disabled={isUpdating}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditSave(document.id, document)}
                          disabled={isUpdating || !editingName.trim()}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleEditCancel}
                          disabled={isUpdating}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <h4 className="font-medium text-gray-900 truncate mb-1">{document.name}</h4>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{formatFileSize(document.size)}</span>
                      <Badge variant="secondary" className="text-xs">
                        {getFileTypeLabel(document.type)}
                      </Badge>
                      <span>•</span>
                      <span>Subido por {document.user.name}</span>
                      <span>•</span>
                      <span>{new Date(document.created_at).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {canEditDocument(document) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditStart(document)}
                        disabled={editingDocumentId === document.id}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                    {canViewDocuments && isFileTypeSupported(document.name) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(document)}
                        title="Vista previa"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {canViewDocuments && (
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(document)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    {canDeleteDocument(document) && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>¿Eliminar documento?</DialogTitle>
                            <DialogDescription>
                              Esta acción no se puede deshacer. El documento "{document.name}" será eliminado permanentemente.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline">Cancelar</Button>
                            <Button
                              onClick={() => handleDelete(document.id, document)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {!isLoading && filteredDocuments.length === 0 && !isUploading && (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm || filterType !== 'all' ? 'No se encontraron documentos' : 'Aún no hay documentos'}
                </h4>
                <p className="text-gray-600">
                  {searchTerm || filterType !== 'all'
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'Sube el primer documento para este trámite'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Upload Area - Only show if user can upload */}
      {(canUploadDocuments || canManageDocuments) && (
        <Card className={`border-2 border-dashed transition-colors ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}>
          <CardContent
            className="p-8 text-center"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${dragActive ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-gray-600'}`} />
              </div>

              <div className="space-y-2">
                <h4 className="text-lg font-semibold text-gray-900">
                  {dragActive ? '¡Suelta los archivos aquí!' : 'Sube tus documentos'}
                </h4>
                <p className="text-gray-600">
                  Arrastra y suelta archivos aquí, o{' '}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    selecciona archivos
                  </button>
                </p>
                <p className="text-xs text-gray-500">
                  Formatos soportados: PDF, Word, Excel, Imágenes (máx. 10MB)
                </p>
              </div>

              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="mt-2"
              >
                {isUploading ? 'Subiendo...' : 'Seleccionar archivos'}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* No permissions message */}
      {!canViewDocuments && (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Sin permisos de acceso
            </h4>
            <p className="text-gray-600">
              No tienes permisos para ver los documentos de este trámite
            </p>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <PreviewDocumentDialog
        document={previewDocument}
        tramiteId={tramite.id}
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
      />
    </div>
  );
};

export default DocumentsTab;
